import { PostReactionType } from '__generated__/graphql/legacy/graphql';
import { Emoji } from 'emoji-mart';
import { useTranslation } from 'react-i18next';

import React, { useRef } from 'react';

import { Button } from '@thinkific/chalk/Button';
import { Smiley } from '@thinkific/chalk/react-icons';
import { Popover } from '@thinkific/chalk/recipes/Popover';

import { ReactionEmoji, getAvailableEmojis } from '../../utils/reactionUtils';
import * as css from './ReactionBar.css';

interface ReactionBarProps {
  onReactionClick: (reactionType: PostReactionType) => void;
}

const ReactionBar = ({ onReactionClick }: ReactionBarProps) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const initialFocusRef = useRef<HTMLButtonElement>(null);

  // Get available emojis from the shared utility
  const availableEmojis = getAvailableEmojis();

  const handleEmojiSelect = (emoji: ReactionEmoji) => {
    // Close the popover by triggering the button click
    triggerRef.current?.click();

    // Use external handler for optimistic updates
    onReactionClick(emoji.graphqlEnum);
  };

  return (
    <div className={css.reactionBarStyle} data-qa="reaction-bar">
      <Popover
        className={css.noPaddingPopoverStyle}
        initialFocusRef={initialFocusRef}
        title={t('components-reactionBar-emoji_picker', 'Choose reaction')}
        trigger={
          <Button
            className={css.addReactionButtonStyle}
            data-qa="reaction-picker-button"
            emphasis="low"
            icon={Smiley}
            iconPosition="lead"
            ref={triggerRef}
            size="sm"
            hiddenLabel
          >
            {t('components-reactionBar-add_reaction', 'Add reaction')}
          </Button>
        }
        triggerRef={triggerRef}
        hiddenTitle
      >
        <div
          className={css.emojiGridStyle}
          role="presentation"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {availableEmojis.map((emoji, index) => (
            <button
              aria-label={emoji.name}
              className={css.emojiButtonStyle}
              key={emoji.id}
              ref={index === 0 ? initialFocusRef : undefined}
              title={emoji.name}
              type="button"
              onClick={() => handleEmojiSelect(emoji)}
            >
              <Emoji emoji={emoji.id} size={24} native tooltip />
            </button>
          ))}
        </div>
      </Popover>
    </div>
  );
};

export default ReactionBar;
